import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './OtpScreen.css';
import Logo from '../components/Logo';
import { storeUserData } from '../components/AuthUtils';

function OtpScreen({ setIsAuthenticated }) {
  const [otp, setOtp] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [remainingTime, setRemainingTime] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [showReferralInput, setShowReferralInput] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [referralError, setReferralError] = useState('');
  const [referralSuccess, setReferralSuccess] = useState('');
  const [token, setToken] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const storedPhoneNumber = localStorage.getItem('phoneNumber');
    if (!storedPhoneNumber) {
      navigate('/');
    } else {
      setPhoneNumber(storedPhoneNumber);
    }
  }, [navigate]);

  useEffect(() => {
    if (remainingTime > 0 && !canResend) {
      const timer = setTimeout(() => {
        setRemainingTime(remainingTime - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (remainingTime === 0) {
      setCanResend(true);
    }
  }, [remainingTime, canResend]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!otp.trim() || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setIsLoading(true);
    setError('');

    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');

    const raw = JSON.stringify({
      phone_number: phoneNumber,
      otp: otp
    });

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: raw,
      redirect: 'follow'
    };

    try {
      const response = await fetch(
        'https://api.rtoxpert.com/api/users/verify-otp',
        requestOptions
      );
      const result = await response.json();

      if (response.ok) {
        localStorage.setItem('user_id', result.user_id);
        localStorage.setItem('user_type', result.user_type);
        localStorage.setItem('token', result.token);
        // Also save to sessionStorage via AuthUtils so isAuthenticated() works
        storeUserData({
          user_id: result.user_id,
          user_type: result.user_type,
          token: result.token
        });
        setToken(result.token);
        setShowReferralInput(true);
      } else {
        setError(result.message || 'Invalid OTP. Please try again.');
      }
    } catch (error) {
      setError('Something went wrong. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResend) return;

    setIsLoading(true);
    setError('');

    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');

    const raw = JSON.stringify({
      user_type: 'vehicle_owner',
      phone_number: phoneNumber
    });

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: raw,
      redirect: 'follow'
    };

    try {
      const response = await fetch(
        'https://api.rtoxpert.com/api/users/select-type',
        requestOptions
      );
      const result = await response.json();

      if (response.ok) {
        setRemainingTime(30);
        setCanResend(false);
      } else {
        setError(result.message || 'Failed to resend OTP. Please try again.');
      }
    } catch (error) {
      setError('Something went wrong. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReferralSubmit = async (e) => {
    e.preventDefault();

    if (!referralCode.trim()) {
      setReferralError('Please enter a referral code');
      return;
    }

    setIsLoading(true);
    setReferralError('');
    setReferralSuccess('');

    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);
    myHeaders.append('Content-Type', 'application/json');

    const raw = JSON.stringify({
      referral_code: referralCode
    });

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: raw,
      redirect: 'follow'
    };

    try {
      const response = await fetch(
        'https://api.rtoxpert.com/api/users/referral-code/validate',
        requestOptions
      );
      const result = await response.json();

      if (response.ok) {
        setReferralSuccess(result.message || 'Referral code applied successfully!');
        setTimeout(() => {
          setIsAuthenticated(true);
          navigate('/home');
        }, 1500);
      } else {
        setReferralError(result.message || 'Invalid referral code. Please try again.');
      }
    } catch (error) {
      setReferralError('Something went wrong. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const skipReferral = () => {
    setIsAuthenticated(true);
    navigate('/home');
  };

  return (
    <div className="otp-screen">
      <Helmet>
        <title>Verify OTP | VAAHANBUDDY - Vehicle Services in India</title>
        <meta name="description" content="Enter your OTP to verify your phone number and access VAAHANBUDDY’s hassle-free RTO services, vehicle insurance, and buy-sell solutions." />
      </Helmet>
      <div className="otp-left">
        <div className="otp-logo"><Logo /></div>
        {!showReferralInput ? (
          <>
            <div className="otp-text">
              <h1>Enter OTP to verify your number</h1>
              <p>We have sent a 6-digit OTP to your phone number {phoneNumber}. Please enter it below.</p>
            </div>
            <form onSubmit={handleSubmit} className="otp-form">
              <div className="input-group">
                <label htmlFor="otp">OTP</label>
                <input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={otp}
                  onChange={e => setOtp(e.target.value)}
                  placeholder="Enter OTP"
                  maxLength={6}
                  disabled={isLoading}
                />
                {error && <p className="error-message">{error}</p>}
              </div>
              <button type="submit" className="verify-btn" disabled={isLoading}>
                {isLoading ? 'Verifying...' : 'Verify'}
              </button>
            </form>
            <p className="resend-text">
              Didn't receive OTP?
              {canResend ? (
                <span
                  className="resend-link"
                  onClick={handleResendOTP}
                  style={{ cursor: 'pointer' }}
                >
                  Resend
                </span>
              ) : (
                <span>Resend in {remainingTime}s</span>
              )}
            </p>
          </>
        ) : (
          <>
            <div className="otp-text">
              <h1>Enter Referral Code (Optional)</h1>
              <p>If you have a referral code, enter it below to receive rewards.</p>
            </div>
            <form onSubmit={handleReferralSubmit} className="otp-form">
              <div className="input-group">
                <label htmlFor="referralCode">Referral Code</label>
                <input
                  id="referralCode"
                  type="text"
                  value={referralCode}
                  onChange={e => setReferralCode(e.target.value)}
                  placeholder="Enter Referral Code"
                  disabled={isLoading}
                />
                {referralError && <p className="error-message">{referralError}</p>}
                {referralSuccess && <p className="success-message">{referralSuccess}</p>}
              </div>
              <button type="submit" className="verify-btn" disabled={isLoading}>
                {isLoading ? 'Applying...' : 'Apply Code'}
              </button>
              <button
                type="button"
                className="skip-btn"
                onClick={skipReferral}
                disabled={isLoading}
              >
                Skip
              </button>
            </form>
          </>
        )}
      </div>
      <div className="otp-right">
        <div
          className="illustration"
          role="img"
          aria-label="Car Service Illustration"
        />
      </div>
    </div>
  );
}

export default OtpScreen;
